import React, { useState } from 'react';
import { User, Globe, SlidersHorizontal, Lock, Zap } from 'lucide-react';
import { SubscriberVsFreeMatrix } from './SubscriberVsFreeMatrix';
import { RevenueShareTerms } from './RevenueShareTerms';
import { ComparisonEvidenceModal } from './ComparisonEvidenceModal';
import {
  COMPETITORS,
  COMPARISON_ROWS,
  COMPARISON_LAST_REVIEWED,
  COMPARISON_FOOTNOTE,
  ALL_SOURCES,
} from '../../data/comparisonMatrix';
import { trackEvent } from '../../utils/telemetry';

// Group rows by section while preserving original order so the table renders
// 4 section headers (User Economics, Privacy & Data, Search Infrastructure,
// Trust & Governance) with their rows beneath each header.
const ROWS_BY_SECTION = COMPARISON_ROWS.reduce((acc, row) => {
  const last = acc[acc.length - 1];
  if (last && last.section === row.section) {
    last.rows.push(row);
  } else {
    acc.push({ section: row.section, rows: [row] });
  }
  return acc;
}, []);

export const MenuOverlay = ({
  show,
  onClose,
  view,
  setView,
  isMobile = false,
  displayUsername = '',
}) => {
  // Click-through state for the evidence modal.
  // Holds { cell, competitorName, rowLabel } when a rating is clicked; null otherwise.
  const [activeEvidence, setActiveEvidence] = useState(null);
  // Whether the "View all cited sources" library is open.
  const [showSourceLibrary, setShowSourceLibrary] = useState(false);

  if (!show) return null;

  const handleClose = () => {
    onClose();
    setView('main');
    setActiveEvidence(null);
    setShowSourceLibrary(false);
  };

  const openEvidence = (cell, competitorName, rowLabel) => {
    setActiveEvidence({ cell, competitorName, rowLabel });
    trackEvent('comparison_evidence_open', {
      competitor: competitorName,
      row: rowLabel,
      colour: cell.colour,
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" data-testid="menu-overlay">
      <div className="absolute inset-0 bg-black/40" onClick={handleClose} />
      
      {view === 'main' ? (
        <div className={`relative z-10 w-full ${isMobile ? 'max-w-sm mx-3 px-5 py-6' : 'max-w-5xl mx-4 px-10 py-8'} rounded-2xl`} style={{ backgroundColor: 'rgba(8, 12, 24, 0.98)', backdropFilter: 'blur(20px)', border: '1px solid rgba(0, 200, 255, 0.15)' }} data-testid="menu-panel">
          <button onClick={handleClose} className="absolute top-5 right-6 text-gray-300 hover:text-white transition-colors text-2xl" data-testid="menu-close">&times;</button>
          
          <div className="flex items-center gap-4 mb-2">
            <div className={`${isMobile ? 'w-10 h-10' : 'w-14 h-14'} rounded-full bg-gray-500/60 border-2 border-gray-400 flex items-center justify-center flex-shrink-0`}>
              <User className={isMobile ? "h-6 w-6 text-gray-200" : "h-8 w-8 text-gray-200"} />
            </div>
            <h2 className={`${isMobile ? 'text-lg' : 'text-3xl'} font-semibold text-white tracking-tight`} data-testid="menu-greeting">{isMobile ? 'Your Menu' : (displayUsername ? `${displayUsername}, this is your Menu` : 'This is your Menu')}</h2>
          </div>
          
          <div className={`flex ${isMobile ? 'flex-col gap-3 mt-6' : 'flex-wrap justify-center gap-6 mt-12'}`}>
            {[
              { name: 'Revenue Share Terms', id: 'revenue-terms' },
              { name: 'Subscriber vs Free Site Comparison', id: 'subscriber-vs-free' },
              { name: 'Why Search.Inc', id: 'why-search' },
              { name: 'Search Engine Comparison Table', id: 'comparison' },
            ].map((item) => (
              <button
                key={item.id}
                onClick={() => {
                  if (item.id === 'revenue-terms') {
                    trackEvent('view_revenue_share', { source: 'menu' });
                  }
                  setView(item.id);
                }}
                className={`${isMobile ? 'w-full h-16' : 'w-44 h-36'} rounded-lg text-center text-white ${isMobile ? 'text-sm' : 'text-base'} font-medium transition-all duration-200 hover:bg-gray-400/50 cursor-pointer hover:scale-[1.03] flex items-center justify-center p-4`}
                style={{ backgroundColor: 'rgba(160, 165, 175, 0.45)' }}
                data-testid={`menu-item-${item.id}`}
              >
                {item.name}
              </button>
            ))}
          </div>
        </div>
      ) : view === 'comparison' ? (
        <div className={`relative z-10 w-full ${isMobile ? 'mx-2 max-h-[95vh]' : 'max-w-7xl mx-4 max-h-[90vh]'} rounded-2xl flex flex-col`} style={{ backgroundColor: 'rgba(30, 35, 45, 0.95)', backdropFilter: 'blur(20px)', border: '1px solid rgba(255,255,255,0.1)' }} data-testid="comparison-panel">
          <button onClick={() => setView('main')} className="absolute top-5 right-6 text-gray-300 hover:text-white transition-colors text-xl z-20" data-testid="comparison-back-btn">&#x21A9;</button>
          
          <div className={`${isMobile ? 'px-4 pt-5 pb-3' : 'px-8 pt-8 pb-4'}`}>
            <h2 className={`${isMobile ? 'text-xl' : 'text-3xl'} font-bold text-center text-white mb-2`}>So, What's the Difference?</h2>
            <div className={`flex justify-center gap-6 ${isMobile ? 'text-xs' : 'text-sm'}`}>
              <span className="flex items-center gap-2"><span className="w-4 h-4 rounded-full bg-emerald-500"></span><span className="text-emerald-400">= Good</span></span>
              <span className="flex items-center gap-2"><span className="w-4 h-4 rounded-full bg-red-500"></span><span className="text-red-400">= Bad</span></span>
              <span className="flex items-center gap-2"><span className="w-4 h-4 rounded-full bg-yellow-500"></span><span className="text-yellow-400">= Partial</span></span>
            </div>
          </div>
          
          <div className="overflow-auto flex-1 px-2 pb-6">
            <table className={`w-full border-collapse ${isMobile ? 'text-xs' : 'text-sm'}`} data-testid="comparison-table">
              <thead className="sticky top-0 z-10">
                <tr>
                  <th className={`text-left p-2 bg-gray-800/90 text-gray-300 font-medium rounded-tl-lg ${isMobile ? 'min-w-[120px]' : 'min-w-[200px]'}`}></th>
                  {COMPETITORS.map((c, i) => (
                    <th
                      key={c.key}
                      className={`p-2 ${c.key === 'searchinc' ? 'bg-cyan-600/90' : 'bg-gray-700/90'} text-white font-bold text-center ${isMobile ? 'min-w-[70px]' : 'min-w-[110px]'} ${i === COMPETITORS.length - 1 ? 'rounded-tr-lg' : ''}`}
                      data-testid={`comparison-col-${c.key}`}
                    >
                      <span className={isMobile ? 'text-xs' : 'text-base'}>{c.name}</span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {ROWS_BY_SECTION.map((group) => (
                  <React.Fragment key={group.section}>
                    {/* Section header row — spans all columns */}
                    <tr data-testid={`comparison-section-${group.section.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`}>
                      <td
                        colSpan={1 + COMPETITORS.length}
                        className={`bg-gray-900/70 text-cyan-300 font-bold uppercase tracking-wider ${isMobile ? 'text-[11px] py-2 px-2' : 'text-xs py-2 px-3'} border-t border-gray-700/50`}
                      >
                        {group.section}
                      </td>
                    </tr>
                    {group.rows.map((row, rowIdx) => (
                      <tr
                        key={`${group.section}-${row.label}`}
                        className={rowIdx % 2 === 0 ? 'bg-gray-800/40' : 'bg-gray-800/20'}
                        data-testid={`comparison-row-${row.label.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`}
                      >
                        <td className={`p-2 text-gray-200 font-medium border-r border-gray-700/50 ${isMobile ? 'text-xs' : ''}`}>
                          {row.label}
                        </td>
                        {COMPETITORS.map((c, i) => {
                          const cell = row.cells[c.key];
                          const dotClasses =
                            cell.colour === 'green' ? 'bg-emerald-500 shadow-emerald-500/50 shadow-md' :
                            cell.colour === 'red' ? 'bg-red-500 shadow-red-500/50 shadow-md' :
                            'bg-yellow-500 shadow-yellow-500/50 shadow-md';
                          const textColour =
                            cell.colour === 'green' ? 'text-emerald-300' :
                            cell.colour === 'red' ? 'text-red-300' :
                            'text-yellow-300';
                          return (
                            <td
                              key={c.key}
                              className={`p-2 text-center border-r border-gray-700/30 ${i === 0 ? 'bg-cyan-900/20' : ''}`}
                            >
                              <button
                                type="button"
                                onClick={() => openEvidence(cell, c.name, row.label)}
                                className="w-full flex flex-col items-center gap-1 cursor-pointer hover:bg-white/5 rounded-md transition-colors py-1 focus:outline-none focus:ring-2 focus:ring-cyan-400/60"
                                data-testid={`comparison-cell-${c.key}-${row.label.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`}
                                aria-label={`${c.name} · ${row.label} · ${cell.display}. Click for supporting evidence.`}
                              >
                                <span className={`w-3 h-3 rounded-full ${dotClasses}`}></span>
                                <span className={`${isMobile ? 'text-[10px]' : 'text-xs'} ${textColour}`}>{cell.display}</span>
                              </button>
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
            {/* Footer — verbatim footnote + last reviewed + view all sources link */}
            <div className={`mt-5 text-center text-gray-400 ${isMobile ? 'text-[11px] px-2' : 'text-xs px-4'}`} data-testid="comparison-footer">
              <p className="leading-relaxed mb-2">{COMPARISON_FOOTNOTE}</p>
              <p>
                <span data-testid="comparison-last-reviewed">Last reviewed {COMPARISON_LAST_REVIEWED}</span>
                <span className="mx-2">·</span>
                <button
                  type="button"
                  onClick={() => { setShowSourceLibrary(true); trackEvent('comparison_source_library_open', {}); }}
                  className="text-cyan-400 hover:text-cyan-300 underline underline-offset-2"
                  data-testid="comparison-source-library-link"
                >
                  View all {ALL_SOURCES.length} cited sources
                </button>
              </p>
            </div>
          </div>
        </div>
      ) : view === 'why-search' ? (
        <div className={`relative z-10 w-full ${isMobile ? 'mx-2 max-h-[95vh]' : 'max-w-[95vw] mx-4 max-h-[90vh]'} rounded-2xl flex flex-col overflow-hidden`} style={{ backgroundColor: 'rgba(8, 12, 24, 0.98)', backdropFilter: 'blur(20px)', border: '1px solid rgba(0, 200, 255, 0.15)' }} data-testid="why-search-panel">
          <button onClick={() => setView('main')} className="absolute top-4 right-5 text-gray-400 hover:text-white transition-colors text-xl z-20" data-testid="why-search-back-btn">&#x21A9;</button>
          
          <div className={`overflow-y-auto ${isMobile ? 'p-4' : 'px-10 py-6'} policy-content`}>
            {/* Top: Search.Inc brand mark + founding quote · IJ-175 · 2026-07-30 */}
            <div className="text-center mb-6">
              <img
                src="/searchinc-logo.png"
                alt="Search.Inc — Actually not evil"
                style={{ height: isMobile ? 22 : 32 }}
                className="mx-auto mb-3 select-none"
                draggable={false}
                data-testid="menu-why-search-brand-logo"
              />
              <p className={`${isMobile ? 'text-sm' : 'text-lg'} text-gray-200 font-light leading-relaxed mx-auto`} style={{ maxWidth: '90%' }}>
                We founded Search.Inc to challenge a system where companies exploit your personal data for obscene profits. We aim to restore balance creating a society where companies profit fairly, share wealth, & respect your privacy. As we grow so do our most engaged & loyal customers
              </p>
            </div>

            {/* Cyan divider line */}
            <div className="w-full h-px bg-gradient-to-r from-transparent via-cyan-400 to-transparent mb-6"></div>

            {/* 4 Column Grid */}
            <div className={`grid ${isMobile ? 'grid-cols-1 gap-4' : 'grid-cols-4 gap-4'} mb-6 items-stretch`}>
              
              {/* Column 1: Control Centre & Privacy */}
              <div className="relative rounded-xl overflow-hidden flex flex-col" style={{ backgroundColor: 'rgba(12, 18, 35, 0.95)', border: '1px solid rgba(0, 180, 220, 0.15)' }}>
                <div className={`${isMobile ? 'p-4' : 'p-5'} flex-1`}>
                  <div className="flex flex-col items-center mb-4">
                    <Lock className="w-8 h-8 text-cyan-400 mb-3" />
                    <h3 className="text-white font-bold text-center text-base">Control Centre<br/>& Privacy</h3>
                  </div>
                  <ul className="space-y-3 text-[15px] text-gray-300">
                    <li className="flex items-start gap-2"><span className="text-cyan-400 mt-1 text-xs">&#9656;</span> Complete anonymity. Individual not connectable to username</li>
                    <li className="flex items-start gap-2"><span className="text-cyan-400 mt-1 text-xs">&#9656;</span> We can't sell your search history. You control what data we can use and what we can't</li>
                    <li className="flex items-start gap-2"><span className="text-cyan-400 mt-1 text-xs">&#9656;</span> Your data is encrypted 100% of the time.</li>
                    <li className="flex items-start gap-2"><span className="text-cyan-400 mt-1 text-xs">&#9656;</span> Reduced Risk of Cyber Attacks</li>
                    <li className="flex items-start gap-2"><span className="text-cyan-400 mt-1 text-xs">&#9656;</span> 60-day data retention policy if you don't delete your data we will.</li>
                    <li className="flex items-start gap-2"><span className="text-cyan-400 mt-1 text-xs">&#9656;</span> Protection Against Tracking & Data Harvesting</li>
                    <li className="flex items-start gap-2"><span className="text-cyan-400 mt-1 text-xs">&#9656;</span> Mitigation of AI-Based Profiling & Manipulation</li>
                  </ul>
                </div>
                <div className="h-1 bg-cyan-400 w-full mt-auto"></div>
              </div>

              {/* Column 2: User-Centric Design */}
              <div className="relative rounded-xl overflow-hidden flex flex-col" style={{ backgroundColor: 'rgba(12, 18, 35, 0.95)', border: '1px solid rgba(0, 180, 220, 0.15)' }}>
                <div className={`${isMobile ? 'p-4' : 'p-5'} flex-1`}>
                  <div className="flex flex-col items-center mb-4">
                    <SlidersHorizontal className="w-8 h-8 text-purple-400 mb-3" />
                    <h3 className="text-white font-bold text-center text-base">User-Centric<br/>Design</h3>
                  </div>
                  <ul className="space-y-3 text-[15px] text-gray-300">
                    <li className="flex items-start gap-2"><span className="text-cyan-400 mt-1 text-xs">&#9656;</span> Our AI Agent / Search Engine hybrid is intuitive & driven by proprietary Search.Inc design</li>
                    <li className="flex items-start gap-2"><span className="text-cyan-400 mt-1 text-xs">&#9656;</span> Our User Experience design is uniquely different yet exceedingly simple.</li>
                    <li className="flex items-start gap-2"><span className="text-cyan-400 mt-1 text-xs">&#9656;</span> Our revolutionary Control Centre gives users complete control with the simplest of user interfaces</li>
                    <li className="flex items-start gap-2"><span className="text-cyan-400 mt-1 text-xs">&#9656;</span> Results are precise and how they get represented is up to the user with different features allowing for different views.</li>
                    <li className="flex items-start gap-2"><span className="text-cyan-400 mt-1 text-xs">&#9656;</span> Search.Inc Intent-AI-Agent delivers 90%+ accuracy and will only get better over time.</li>
                  </ul>
                </div>
                <div className="h-1 bg-cyan-400 w-full mt-auto"></div>
              </div>

              {/* Column 3: Unprecedented Accuracy */}
              <div className="relative rounded-xl overflow-hidden flex flex-col" style={{ backgroundColor: 'rgba(12, 18, 35, 0.95)', border: '1px solid rgba(0, 180, 220, 0.15)' }}>
                <div className={`${isMobile ? 'p-4' : 'p-5'} flex-1`}>
                  <div className="flex flex-col items-center mb-4">
                    <Zap className="w-8 h-8 text-yellow-400 mb-3" />
                    <h3 className="text-white font-bold text-center text-base">Unprecedented<br/>Accuracy</h3>
                  </div>
                  <ul className="space-y-3 text-[15px] text-gray-300">
                    <li className="flex items-start gap-2"><span className="text-cyan-400 mt-1 text-xs">&#9656;</span> Bringing back equitable competition for businesses (eg: Domestic, International / Small to Large)</li>
                    <li className="flex items-start gap-2"><span className="text-cyan-400 mt-1 text-xs">&#9656;</span> Limits on Social and Political Bias</li>
                    <li className="flex items-start gap-2"><span className="text-cyan-400 mt-1 text-xs">&#9656;</span> Results are then ranked on accuracy / relevance not on who pays to be there</li>
                    <li className="flex items-start gap-2"><span className="text-cyan-400 mt-1 text-xs">&#9656;</span> The more data you share via your Username, the better the results.</li>
                    <li className="flex items-start gap-2"><span className="text-cyan-400 mt-1 text-xs">&#9656;</span> Use the accuracy score to guide data sharing for optimal results</li>
                  </ul>
                </div>
                <div className="h-1 bg-cyan-400 w-full mt-auto"></div>
              </div>

              {/* Column 4: Search.Inc Profit Share */}
              <div className="relative rounded-xl overflow-hidden flex flex-col" style={{ backgroundColor: 'rgba(12, 18, 35, 0.95)', border: '1px solid rgba(0, 180, 220, 0.15)' }}>
                <div className={`${isMobile ? 'p-4' : 'p-5'} flex-1`}>
                  <div className="flex flex-col items-center mb-4">
                    <Globe className="w-8 h-8 text-red-400 mb-3" />
                    <h3 className="text-white font-bold text-center text-base">Search.Inc<br/>Revenue Share</h3>
                  </div>
                  <ul className="space-y-3 text-[15px] text-gray-300">
                    <li className="flex items-start gap-2"><span className="text-cyan-400 mt-1 text-xs">&#9656;</span> The only Revenue Share of its kind in the world</li>
                    <li className="flex items-start gap-2"><span className="text-cyan-400 mt-1 text-xs">&#9656;</span> Earn Value Credits by using Search.Inc</li>
                    <li className="flex items-start gap-2"><span className="text-cyan-400 mt-1 text-xs">&#9656;</span> At 0.5% market share of subscribers, the top 10,000 Value Credit earners earn $10k (see full terms)</li>
                    <li className="flex items-start gap-2"><span className="text-cyan-400 mt-1 text-xs">&#9656;</span> At 1% market share 20k receive $10k.</li>
                    <li className="flex items-start gap-2"><span className="text-cyan-400 mt-1 text-xs">&#9656;</span> At 1.5% 30k get $10k etc.</li>
                    <li className="flex items-start gap-2"><span className="text-cyan-400 mt-1 text-xs">&#9656;</span> At 4% market share we would be sharing almost $1 billion to 80k most engaged users</li>
                    <li className="flex items-start gap-2"><span className="text-cyan-400 mt-1 text-xs">&#9656;</span> More users benefit as our market share grows.</li>
                  </ul>
                </div>
                <div className="h-1 bg-cyan-400 w-full mt-auto"></div>
              </div>
            </div>

            {/* Bottom tagline bar */}
            <div className="w-full py-3 text-center border-t border-cyan-400/30">
              <p className={`${isMobile ? 'text-xs' : 'text-base'} font-semibold text-gray-200 tracking-wide`}>
                Unprecedented User Control & Unparalleled Security. Delivered Ethically & Sustainably
              </p>
            </div>
          </div>
        </div>
      ) : view === 'revenue-terms' ? (
        <RevenueShareTerms onBack={() => setView('main')} isMobile={isMobile} />
      ) : view === 'subscriber-vs-free' ? (
        <SubscriberVsFreeMatrix
          onBack={() => setView('main')}
          isMobile={isMobile}
          onNavigate={(target) => setView(target)}
        />
      ) : null}

      {/* Click-through evidence modal — opens when a comparison cell is clicked */}
      {activeEvidence && (
        <ComparisonEvidenceModal
          cell={activeEvidence.cell}
          competitorName={activeEvidence.competitorName}
          rowLabel={activeEvidence.rowLabel}
          onClose={() => setActiveEvidence(null)}
          isMobile={isMobile}
        />
      )}

      {/* "View all cited sources" library modal */}
      {showSourceLibrary && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center"
          data-testid="comparison-source-library-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="source-library-title"
        >
          <div className="absolute inset-0 bg-black/70" onClick={() => setShowSourceLibrary(false)} />
          <div
            className={`relative z-10 ${isMobile ? 'mx-3 w-[94vw] max-h-[85vh]' : 'mx-4 max-w-3xl w-full max-h-[85vh]'} rounded-2xl p-6 flex flex-col`}
            style={{
              backgroundColor: 'rgba(20, 25, 35, 0.98)',
              backdropFilter: 'blur(16px)',
              border: '1px solid rgba(255,255,255,0.12)',
            }}
          >
            <button
              onClick={() => setShowSourceLibrary(false)}
              className="absolute top-4 right-5 text-gray-400 hover:text-white transition-colors text-xl"
              data-testid="comparison-source-library-close"
              aria-label="Close source library"
            >
              &times;
            </button>
            <h3 id="source-library-title" className="text-lg font-bold text-white mb-1">All cited sources</h3>
            <p className="text-xs text-gray-500 mb-4">{ALL_SOURCES.length} sources · Last reviewed {COMPARISON_LAST_REVIEWED}</p>
            <ul className="space-y-2 overflow-y-auto pr-2">
              {ALL_SOURCES.map((s, i) => (
                <li key={s.id} className="text-sm" data-testid={`source-library-item-${i}`}>
                  <a
                    href={s.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-cyan-400 hover:text-cyan-300 underline underline-offset-2"
                  >
                    {s.title}
                  </a>
                  <span className="text-gray-500 ml-2 text-xs">[{s.id}]</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};

export default MenuOverlay;
